import { coreTypes } from "@core/core-types.di";
import { IndicatorAlertSettingsDto } from "@core/data/dto/indicator-alert-settings.dto";
import type { Http } from "@core/data/infrastructures/http/http";
import { IndicatorAlertSettingsMapper } from "@core/data/mappers/indicator-alert-settings.mapper";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { IndicatorAlertSettings } from "@core/domain/models/indicator-alert-settings.model";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";

@injectable()
export class IndicatorAlertSettingsDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http) private readonly http: Http,
        @inject(IndicatorAlertSettingsMapper)
        private readonly mapper: IndicatorAlertSettingsMapper,
    ) {}

    async fetchMy(): Promise<Either<FallbackError, IndicatorAlertSettings>> {
        const result = await this.http.get<IndicatorAlertSettingsDto>(
            "/indicator_alert_settings/me/",
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const dto = plainToClass(
                    IndicatorAlertSettingsDto,
                    response.data,
                );
                return (
                    this.mapper.map(dto) ?? IndicatorAlertSettings.defaults()
                );
            });
    }

    async updateMy(
        settings: IndicatorAlertSettings,
    ): Promise<Either<FallbackError, IndicatorAlertSettings>> {
        const body = this.mapper.toBody(settings);

        const result = await this.http.patch<
            IndicatorAlertSettingsDto,
            typeof body
        >("/indicator_alert_settings/me/", body);

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const dto = plainToClass(
                    IndicatorAlertSettingsDto,
                    response.data,
                );
                return this.mapper.map(dto) ?? settings;
            });
    }
}
